import React, { useRef, useEffect, useMemo } from "react";
import { Container, Sprite } from "@pixi/react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import LogoController from "./LogoController";
import { safeInit } from "../../../../utilities";
import { useSlot } from "../../../../Contexts/SlotRegistryContext";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

// Device-specific multipliers and position offsets
const DEVICE_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 3.5,
      xOffsetMultiplier: -3.5,
      yOffsetMultiplier: 1.5,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 3.0,
      xOffsetMultiplier: -3.45,
      yOffsetMultiplier: 1.5,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 2.1,
      xOffsetMultiplier: -3.4,
      yOffsetMultiplier: 1.5,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    deviceMultiplier: 2.5,
    xOffsetMultiplier: -3.8,
    yOffsetMultiplier: 1.5,
  },
};

const isBoneInjection = FEATURES_CONFIG.BACKGROUND.REELSET_BONE_INJECTION;

/**
 * LogoLandscape
 *
 * @param {string} target - "base" or "feature" — determines which bone slot to inject into
 * @param {string} renderMode - "spine" or "static"
 */
const LogoLandscape = ({ target = "base", renderMode = "static" }) => {
  const {
    device,
    orientation,
    symbolScale,
    symbolWidth,
    symbolHeight,
    isMobileSmall,
  } = useDimensions();
  const logoControllerRef = useRef(null);
  const containerRef = useRef(null);

  const texture = ASSET_NAME.logo ? PIXI.Assets.get(ASSET_NAME.logo) : null;

  // Get device configuration - use real device for MOBILE_SMALL support
  const deviceConfig = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    if (realDevice === PixiSettings.DEVICE.DESKTOP) {
      return DEVICE_CONFIG[realDevice];
    }
    return (
      DEVICE_CONFIG[realDevice]?.[orientation] || {
        deviceMultiplier: 1.0,
        xOffsetMultiplier: -4.0,
        yOffsetMultiplier: 1.35,
      }
    );
  }, [device, orientation, isMobileSmall]);

  const scaledDimensions = useMemo(() => {
    // Spine mode: use symbol-based reference size (texture is not a PIXI.Texture for spine assets)
    if (renderMode === "spine") {
      const fallbackWidth = symbolWidth * 2.5;
      const fallbackHeight = symbolHeight * 0.8;
      return {
        scaledWidth:
          fallbackWidth * deviceConfig.deviceMultiplier * symbolScale,
        scaledHeight:
          fallbackHeight * deviceConfig.deviceMultiplier * symbolScale,
      };
    } else if (texture?.width && texture?.height) {
      // Static mode: use actual texture dimensions
      const nativeWidth = texture.width;
      const nativeHeight = texture.height;
      return {
        scaledWidth:
          nativeWidth * deviceConfig.deviceMultiplier * symbolScale,
        scaledHeight:
          nativeHeight * deviceConfig.deviceMultiplier * symbolScale,
      };
    }
    return { scaledWidth: 0, scaledHeight: 0 };
  }, [
    texture,
    deviceConfig.deviceMultiplier,
    symbolScale,
    renderMode,
    symbolWidth,
    symbolHeight,
  ]);

  // Inject container into background spine's logo bone slot via SlotRegistry
  const registrySlotName = `background.logo_${target}_landscape`;
  const logoSlotContainer = useSlot(registrySlotName);
  useEffect(() => {
    if (!isBoneInjection) return;
    if (!logoSlotContainer || !containerRef.current) return;
    const node = containerRef.current;
    logoSlotContainer.addChild(node);
    return () => {
      if (node.parent === logoSlotContainer) {
        logoSlotContainer.removeChild(node);
      }
    };
  }, [logoSlotContainer]);

  // Create controller when showing, cleanup when hiding
  useEffect(() => {
    // Bone injection: always create (alpha handles visibility via setLogoMode)
    // Non-injection: only in landscape orientation
    const shouldShow = isBoneInjection
      ? renderMode === "spine"
      : renderMode === "spine" &&
        orientation !== PixiSettings.ORIENTATION.PORTRAIT;

    if (!shouldShow) {
      if (logoControllerRef.current) {
        logoControllerRef.current.cleanup();
        logoControllerRef.current = null;
      }
      return;
    }

    if (containerRef.current && !logoControllerRef.current) {
      const { scaledWidth, scaledHeight } = scaledDimensions;

      safeInit(() => {
        logoControllerRef.current = new LogoController(
          containerRef.current,
          scaledWidth,
          scaledHeight,
          deviceConfig,
        );
        logoControllerRef.current.setVisible(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode, orientation]);

  // Update dimensions on resize (only when controller exists and is initialized)
  useEffect(() => {
    const controller = logoControllerRef.current;
    if (!controller?.isInitialized) return;

    const { scaledWidth, scaledHeight } = scaledDimensions;
    controller.updateDimensions(scaledWidth, scaledHeight, deviceConfig);
    controller.setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode, scaledDimensions, deviceConfig, orientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (logoControllerRef.current) {
        logoControllerRef.current.cleanup();
        logoControllerRef.current = null;
      }
    };
  }, []);

  // Position — bone injection: (0,0) relative to bone; fallback: manual offsets
  const logoPosition = useMemo(() => {
    if (isBoneInjection) {
      return { x: 0, y: 0 };
    }
    const x = symbolWidth * deviceConfig.xOffsetMultiplier;
    const y = -(symbolHeight * deviceConfig.yOffsetMultiplier);
    return { x, y };
  }, [
    symbolWidth,
    symbolHeight,
    deviceConfig.xOffsetMultiplier,
    deviceConfig.yOffsetMultiplier,
  ]);

  // Don't render in portrait (landscape logo only) — unless bone-injected (alpha handles visibility)
  if (!isBoneInjection && orientation === PixiSettings.ORIENTATION.PORTRAIT) {
    return null;
  }
  if (renderMode === "static" && !texture) {
    return null;
  }

  const { scaledWidth, scaledHeight } = scaledDimensions;

  return (
    <Container
      ref={containerRef}
      x={logoPosition.x}
      y={logoPosition.y}
      alpha={
        isBoneInjection && orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? 0.001
          : 1
      }
      zIndex={5}
      name={`logo_landscape_${target}`}
      sortableChildren={true}
    >
      {renderMode === "static" && texture && (
        <Sprite
          texture={texture}
          width={scaledWidth}
          height={scaledHeight}
          anchor={0.5}
        />
      )}
    </Container>
  );
};

export default LogoLandscape;
